import {useMemo, useState} from 'react';
import type {ChangeEvent, ReactElement} from 'react';
import {Button, Size as ButtonSize, Type as ButtonType} from '@Components/button/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2/toggle-switch';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {AiAgentMode} from '@/ai-chat/providers/ai-chat-provider.types';
import {AgentModePill} from '@/ai-chat/components/agent-mode-pill/agent-mode-pill';
import type {AgentOverrideRow, ForcedImageProvider, ForcedTextRemovalMethod, MockCredits, OverrideSelectRowProps} from './ai-chat-super-user-options.types';
import {
  clearSuperUserVariantOverrides,
  createOverrideSelectHandler,
  downloadUserAttachments,
  getForcedImageProviderOptions,
  getForcedTextRemovalMethodOptions,
  getInitialAgents,
  getInitialForcedImageProvider,
  getInitialForcedTextRemovalMethod,
  getInitialMockCredits,
  getMockCreditsOptions,
  isForcedImageProvider,
  isForcedTextRemovalMethod,
  isMockCredits,
  setSuperUserForcedImageProvider,
  setSuperUserForcedTextRemovalMethod,
  setSuperUserMockCredits,
  setSuperUserVariantOverride,
} from './ai-chat-super-user-options.library';
import styles from './ai-chat-super-user-options.module.scss';

const WEIGHTED_PICK_VALUE = '';

type AgentChangeHandler = (agentType: string) => (event: ChangeEvent<HTMLSelectElement>) => void;

export function AiChatSuperUserOptions(): ReactElement | null {
  const {state, actions} = useAiChat();
  const initialAgents = useMemo<AgentOverrideRow[]>(() => getInitialAgents(), []);
  const [agents, setAgents] = useState<AgentOverrideRow[]>(initialAgents);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingAttachments, setIsDownloadingAttachments] = useState<boolean>(false);
  const [forcedProvider, setForcedProvider] = useState<ForcedImageProvider>(() => getInitialForcedImageProvider());
  const [forcedTextRemovalMethod, setForcedTextRemovalMethod] = useState<ForcedTextRemovalMethod>(() => getInitialForcedTextRemovalMethod());
  const [mockCredits, setMockCredits] = useState<MockCredits>(() => getInitialMockCredits());

  if (!state.isSuperUser) {
    return null;
  }

  const handleChange: AgentChangeHandler =
    (agentType: string) =>
    (event: ChangeEvent<HTMLSelectElement>): void => {
      const value = event.target.value;
      void setSuperUserVariantOverride(agentType, value).then(
        () => {
          setError(null);
          setAgents((current) =>
            current.map((row) => (row.agentType === agentType ? {...row, currentAlias: value === WEIGHTED_PICK_VALUE ? null : value} : row))
          );
        },
        () => {
          setError(window.i18next.t('pmwjs_variant_save_failed'));
        }
      );
    };

  const handleClearAll = (): void => {
    void clearSuperUserVariantOverrides().then(
      () => {
        setError(null);
        setAgents((current) => current.map((row) => ({...row, currentAlias: null})));
      },
      () => {
        setError(window.i18next.t('pmwjs_variant_save_failed'));
      }
    );
  };

  const handleShowDeletedChange = (): void => {
    actions.setShowDeletedMessages(!state.showDeletedMessages);
  };

  const handleShowGenerationContextChange = (): void => {
    actions.setHideGenerationContext(!state.hideGenerationContext);
  };

  const handleAgentModeChange = (): void => {
    const nextMode = state.mode === AiAgentMode.AGENT ? AiAgentMode.CHAT : AiAgentMode.AGENT;
    actions.setMode(nextMode);
  };

  const handleForcedProviderChange = createOverrideSelectHandler<ForcedImageProvider>({
    isValidValue: isForcedImageProvider,
    save: setSuperUserForcedImageProvider,
    onSaved: (value: ForcedImageProvider): void => {
      setError(null);
      setForcedProvider(value);
    },
    onError: (): void => {
      setError(window.i18next.t('pmwjs_variant_save_failed'));
    },
  });

  const handleForcedTextRemovalMethodChange = createOverrideSelectHandler<ForcedTextRemovalMethod>({
    isValidValue: isForcedTextRemovalMethod,
    save: setSuperUserForcedTextRemovalMethod,
    onSaved: (value: ForcedTextRemovalMethod): void => {
      setError(null);
      setForcedTextRemovalMethod(value);
    },
    onError: (): void => {
      setError(window.i18next.t('pmwjs_ai_chat_force_text_removal_method_save_failed'));
    },
  });

  const handleMockCreditsChange = createOverrideSelectHandler<MockCredits>({
    isValidValue: isMockCredits,
    save: setSuperUserMockCredits,
    onSaved: (value: MockCredits): void => {
      setError(null);
      setMockCredits(value);
    },
    onError: (): void => {
      setError(window.i18next.t('pmwjs_variant_save_failed'));
    },
  });

  const handleDownloadAttachments = (): void => {
    const chatId = state.currentChatId;
    if (chatId === undefined || isDownloadingAttachments) {
      return;
    }
    setIsDownloadingAttachments(true);
    setError(null);
    downloadUserAttachments(chatId)
      .catch((downloadError: unknown) => {
        const message = downloadError instanceof Error ? downloadError.message : window.i18next.t('pmwjs_super_user_attachments_download_failed');
        setError(message);
      })
      .finally(() => {
        setIsDownloadingAttachments(false);
      });
  };

  return (
    <Dropdown
      id="ai-chat-super-user-options"
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popUpHasCustomWidth
      popupClassName={styles.popup}
      doCloseOnInnerClick={false}
      selector={renderSelector()}
      popup={renderPopup({
        isAgentMode: state.mode === AiAgentMode.AGENT,
        onAgentModeChange: handleAgentModeChange,
        showDeleted: state.showDeletedMessages,
        onShowDeletedChange: handleShowDeletedChange,
        showGenerationContext: !state.hideGenerationContext,
        onShowGenerationContextChange: handleShowGenerationContextChange,
        currentChatId: state.currentChatId,
        isDownloadingAttachments,
        onDownloadAttachments: handleDownloadAttachments,
        forcedProvider,
        onForcedProviderChange: handleForcedProviderChange,
        forcedTextRemovalMethod,
        onForcedTextRemovalMethodChange: handleForcedTextRemovalMethodChange,
        mockCredits,
        onMockCreditsChange: handleMockCreditsChange,
        agents,
        onChangeAgent: handleChange,
        onClearAll: handleClearAll,
        error,
      })}
    />
  );
}

function renderSelector(): ReactElement {
  return (
    <div className={styles.selector}>
      <AgentModePill />
      <Icon
        icon="icon-tools"
        shape={IconShape.SQUARE}
        type={IconType.GHOST_UNCOLORED}
        size={IconSize.SIZE_ICON_16}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_super_user_options_title')}
      />
    </div>
  );
}

interface PopupProps {
  isAgentMode: boolean;
  onAgentModeChange: () => void;
  showDeleted: boolean;
  onShowDeletedChange: () => void;
  showGenerationContext: boolean;
  onShowGenerationContextChange: () => void;
  currentChatId: string | undefined;
  isDownloadingAttachments: boolean;
  onDownloadAttachments: () => void;
  forcedProvider: ForcedImageProvider;
  onForcedProviderChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  forcedTextRemovalMethod: ForcedTextRemovalMethod;
  onForcedTextRemovalMethodChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  mockCredits: MockCredits;
  onMockCreditsChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  agents: AgentOverrideRow[];
  onChangeAgent: AgentChangeHandler;
  onClearAll: () => void;
  error: string | null;
}

interface DownloadAttachmentsButtonProps {
  currentChatId: string | undefined;
  isDownloading: boolean;
  onDownload: () => void;
}

function renderPopup(props: PopupProps): ReactElement {
  return (
    <div className={styles.body}>
      <Text val={window.i18next.t('pmwjs_super_user_options_title')} size={TextSize.SM} bold className={styles.heading} />
      <div className={styles.toggleRow}>
        <ToggleSwitch
          id="ai-chat-agent-mode"
          isChecked={props.isAgentMode}
          onChange={props.onAgentModeChange}
          size={ToggleSwitchV2Size.SMALL}
          elementTitle={window.i18next.t('pmwjs_ai_chat_agent_mode')}
        />
        <Text val={window.i18next.t('pmwjs_ai_chat_agent_mode')} size={TextSize.SM} className={styles.toggleLabel} />
      </div>
      <div className={styles.toggleRow}>
        <ToggleSwitch
          id="ai-chat-show-deleted-messages"
          isChecked={props.showDeleted}
          onChange={props.onShowDeletedChange}
          size={ToggleSwitchV2Size.SMALL}
          elementTitle={window.i18next.t('pmwjs_show_deleted_messages')}
        />
        <Text val={window.i18next.t('pmwjs_show_deleted_messages')} size={TextSize.SM} className={styles.toggleLabel} />
      </div>
      <div className={styles.toggleRow}>
        <ToggleSwitch
          id="ai-chat-show-generation-context"
          isChecked={props.showGenerationContext}
          onChange={props.onShowGenerationContextChange}
          size={ToggleSwitchV2Size.SMALL}
          elementTitle={window.i18next.t('pmwjs_show_generation_context')}
        />
        <Text val={window.i18next.t('pmwjs_show_generation_context')} size={TextSize.SM} className={styles.toggleLabel} />
      </div>
      {renderDownloadAttachmentsButton({
        currentChatId: props.currentChatId,
        isDownloading: props.isDownloadingAttachments,
        onDownload: props.onDownloadAttachments,
      })}
      {renderOverrideSelectRow({
        id: 'ai-chat-force-image-provider',
        label: window.i18next.t('pmwjs_ai_chat_force_image_provider'),
        value: props.forcedProvider,
        options: getForcedImageProviderOptions(),
        onChange: props.onForcedProviderChange,
      })}
      {renderOverrideSelectRow({
        id: 'ai-chat-force-text-removal-method',
        label: window.i18next.t('pmwjs_ai_chat_force_text_removal_method'),
        value: props.forcedTextRemovalMethod,
        options: getForcedTextRemovalMethodOptions(),
        onChange: props.onForcedTextRemovalMethodChange,
      })}
      {renderOverrideSelectRow({
        id: 'ai-chat-mock-credits',
        label: window.i18next.t('pmwjs_ai_chat_mock_credits'),
        value: props.mockCredits,
        options: getMockCreditsOptions(),
        onChange: props.onMockCreditsChange,
      })}
      {renderVariantSection(props.agents, props.onChangeAgent, props.onClearAll)}
      {renderError(props.error)}
    </div>
  );
}

function renderDownloadAttachmentsButton(props: DownloadAttachmentsButtonProps): ReactElement {
  return (
    <Button
      type={ButtonType.SECONDARY}
      size={ButtonSize.SMALL}
      text={window.i18next.t('pmwjs_super_user_download_user_attachments')}
      icon="icon-download"
      isFullWidth
      disabled={props.currentChatId === undefined}
      isLoading={props.isDownloading}
      onClick={props.onDownload}
    />
  );
}

function renderOverrideSelectRow(props: OverrideSelectRowProps): ReactElement {
  return (
    <div className={styles.variantRow}>
      <label htmlFor={props.id} className={styles.toggleLabel}>
        {props.label}
      </label>
      <select id={props.id} className={styles.select} value={props.value} onChange={props.onChange}>
        {props.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function renderVariantSection(agents: AgentOverrideRow[], onChange: AgentChangeHandler, onClearAll: () => void): ReactElement | null {
  if (agents.length === 0) {
    return null;
  }
  return (
    <>
      <div className={styles.divider} aria-hidden />
      <Text val={window.i18next.t('pmwjs_system_prompt_variants')} size={TextSize.SM} bold className={styles.heading} />
      {renderRows(agents, onChange)}
      <div className={styles.footer}>
        <Button type={ButtonType.SECONDARY} size={ButtonSize.SMALL} text={window.i18next.t('pmwjs_clear_all')} onClick={onClearAll} />
        <Text val={window.i18next.t('pmwjs_super_user_overrides_note')} size={TextSize.XS} className={styles.note} />
      </div>
    </>
  );
}

function renderRows(agents: AgentOverrideRow[], handleChange: AgentChangeHandler): ReactElement {
  return (
    <ul className={styles.list}>
      {agents.map((row) => {
        const activeOptions = row.options.filter((option) => option.isActive);
        return (
          <li key={row.agentType} className={styles.variantRow}>
            <code className={styles.agentName}>{row.agentType}</code>
            <select className={styles.select} value={row.currentAlias ?? WEIGHTED_PICK_VALUE} onChange={handleChange(row.agentType)}>
              <option value={WEIGHTED_PICK_VALUE}>{window.i18next.t('pmwjs_variant_weighted_pick')}</option>
              {row.options.map((option) => (
                <option key={option.idAiPrompt} value={option.alias ?? ''} disabled={!option.isActive}>
                  {formatVariantOptionLabel(option.alias, option.weightPct, option.isActive)}
                </option>
              ))}
            </select>
            <span className={styles.meta}>{getVariantCountLabel(activeOptions.length)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function renderError(error: string | null): ReactElement | null {
  if (error === null) {
    return null;
  }
  return <p className={styles.error}>{error}</p>;
}

function formatVariantOptionLabel(alias: string | null, weightPct: number, isActive: boolean): string {
  const aliasLabel = alias ?? window.i18next.t('pmwjs_variant_unnamed');
  const suffix = isActive ? '' : ` · ${window.i18next.t('pmwjs_variant_inactive_suffix')}`;
  return `${aliasLabel} · ${weightPct}%${suffix}`;
}

function getVariantCountLabel(count: number): string {
  const key = count === 1 ? 'pmwjs_variant_count_one' : 'pmwjs_variant_count_other';
  return window.i18next.t(key, {count});
}
